import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Col, Row, Statistic, message, Select } from "antd";
import {
  ShoppingCartOutlined,
  CreditCardOutlined,
  DollarCircleOutlined,
  RiseOutlined,
  FallOutlined,
  FundOutlined,
} from "@ant-design/icons";
import API from "../api";
import { useNavigate } from "react-router-dom";
// import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import "./dashboard.css";
import { setProductSetting } from "../redux/slices/productSettingSlice";
import { useDispatch } from "react-redux";

const { Option } = Select;

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = JSON.parse(localStorage.getItem("userData")) || {};
  const defaultStoreId = userData.storeId || null;

  // State management
  const [dashboardData, setDashboardData] = useState({
    sales: { totalAmount: 0 },
    purchase: { totalAmount: 0 },
    stocks: { totalStockLevel: 0 },
    supplier: { totalSuppliers: 0 },
    profitLoss: { summary: {} },
  });
  const [stores, setStores] = useState([]);
  const [selectedStoreName, setSelectedStoreName] = useState(null);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
  const [isLoading, setLoading] = useState(false);

  // Memoized financial years
  const financialYears = useMemo(() => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1);
    const currentFinancialYearStart =
      currentDate < financialYearStart
        ? currentDate.getFullYear() - 1
        : currentDate.getFullYear();

    const years = [];
    for (let year = 2024; year <= currentFinancialYearStart; year++) {
      years.push(`${year}-${year + 1}`);
    }
    return years;
  }, []);

  // Memoized calculations
  const calculations = useMemo(() => {
    const { sales, profitLoss } = dashboardData;
    const profitBeforeExpenses = profitLoss?.summary?.totalProfit || 0;
    const totalExpenses = profitLoss?.summary?.totalExpenses || 0;
    const profitAfterExpenses = profitBeforeExpenses - totalExpenses;
    const profitMargin =
      sales.totalAmount > 0
        ? (profitBeforeExpenses / sales.totalAmount) * 100
        : 0;

    return {
      profitBeforeExpenses,
      totalExpenses,
      profitAfterExpenses,
      profitMargin,
    };
  }, [dashboardData]);

  // Fetch store list
  const fetchStores = useCallback(async () => {
    try {
      const response = await API.get("/stores");
      setStores(response.data);

      const defaultStore = response.data.find(
        (store) => store._id === defaultStoreId
      );
      setSelectedStoreName(defaultStore?.name || null);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to fetch stores.");
    }
  }, [defaultStoreId]);

  // Fetch dashboard data
  const fetchData = useCallback(
    async (storeName, financialYear) => {
      setLoading(true);
      try {
        const selectedStore = storeName
          ? stores.find((store) => store.name === storeName)
          : null;
        const storeId = selectedStore?._id;
        const params = {
          ...(storeId && { storeId }),
          ...(financialYear && { financialYear }),
        };

        const [
          salesResponse,
          purchaseResponse,
          stocksResponse,
          suppliersResponse,
          profitLossResponse,
        ] = await Promise.all([
          API.get("/dashboard/sales/amount", { params }),
          API.get("/dashboard/purchase/amount", { params }),
          API.get("/dashboard/stocks", { params }),
          API.get("/dashboard/suppliers", { params }),
          API.get("/dashboard/profit", { params }),
        ]);

        setDashboardData({
          sales: salesResponse.data,
          purchase: purchaseResponse.data,
          stocks: stocksResponse.data,
          supplier: suppliersResponse.data,
          profitLoss: profitLossResponse.data,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    },
    [stores]
  );

  // Event handlers
  const handleStoreChange = useCallback((value) => {
    setSelectedStoreName(value === "all" ? null : value);
  }, []);

  const handleFinancialYearChange = useCallback((value) => {
    setSelectedFinancialYear(value === "all" ? null : value);
  }, []);

  // Effects
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    if (stores.length > 0) {
      fetchData(selectedStoreName, selectedFinancialYear);
    }
  }, [selectedStoreName, selectedFinancialYear, stores, fetchData]);

  // Card components
  const StatCard = ({
    title,
    value,
    prefix,
    icon: Icon,
    gradient,
    onClick,
    color = "#fff",
  }) => (
    <Card
      hoverable
      bordered={false}
      style={{
        background: gradient,
        color,
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      }}
      title={title}
      extra={<Icon style={{ fontSize: "24px", color }} />}
      onClick={onClick}
    >
      <Statistic
        title={title}
        value={value}
        prefix={prefix}
        style={{ fontSize: "24px", fontWeight: "bold", color }}
      />
    </Card>
  );

  const fetchSettings = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const payload =
        userData?.role === "admin" ? {} : { storeId: userData?.storeId };

      const res = await API.post("/product/setting/get", payload);
      const formatted = res.data.map((item) => ({
        _id: item._id,
        storeId: item.storeId._id,
        storeName: item.storeId.name,
        mfg_exp: item.mfg_exp ? "Yes" : "No",
        size: item.size ? "Yes" : "No",
        engineNumber: item.trackingNumber ? "Yes" : "No",
        batchNumber: item.batchNumber ? "Yes" : "No",
      }));
      dispatch(setProductSetting(formatted));
    } catch (err) {
      message.error("Failed to fetch product settings");
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <div className="dashboard-container">
      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={8}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select a store"
            value={selectedStoreName || "all"}
            onChange={handleStoreChange}
            loading={isLoading}
            disabled={isLoading || userData.role !== "admin"}
          >
            <Option value="all">All Stores</Option>
            {stores.map((store) => (
              <Option key={store._id} value={store.name}>
                {store.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Financial Year"
            value={selectedFinancialYear || "all"}
            onChange={handleFinancialYearChange}
            loading={isLoading}
            disabled={isLoading}
          >
            <Option value="all">All Years</Option>
            {financialYears.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Purchase Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Purchases"
            value={dashboardData.purchase.totalAmount.toFixed(2)}
            prefix={<span style={{ fontSize: "24px", color: "#fff" }}>₹</span>}
            icon={DollarCircleOutlined}
            gradient="linear-gradient(135deg, #ff7e5f, #feb47b)"
            onClick={() => navigate("/purchase")}
          />
        </Col>

        {/* Stocks Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Stock Levels"
            value={dashboardData.stocks.totalStockLevel.toFixed(2)}
            prefix={<FundOutlined style={{ color: "#fff" }} />}
            icon={FundOutlined}
            gradient="linear-gradient(135deg, #4facfe, #00f2fe)"
            onClick={() => navigate("/store/stock-report")}
          />
        </Col>

        {/* Sales Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Sales"
            value={dashboardData.sales.totalAmount.toFixed(2)}
            prefix={<span style={{ fontSize: "24px", color: "#fff" }}>₹</span>}
            icon={RiseOutlined}
            gradient="linear-gradient(135deg, #00b4db, #0083b0)"
            onClick={() => navigate("/sales")}
          />
        </Col>

        {/* Financial Overview Card */}
        <Col xs={24} md={24}>
          <Card
            hoverable
            bordered={false}
            style={{
              background:
                "linear-gradient(135deg, #8e54e9, #4776e6, #43cea2, #185a9d)",
              color: "#fff",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
              minHeight: 220,
            }}
            title="Financial Overview"
            extra={
              <DollarCircleOutlined
                style={{ fontSize: "32px", color: "#fff" }}
              />
            }
            onClick={() => navigate("/sales/profit")}
          >
            <Row gutter={[16, 16]}>
              {[
                {
                  title: "Total Profit",
                  value: calculations.profitBeforeExpenses,
                },
                { title: "Total Expenses", value: calculations.totalExpenses },
                {
                  title: "Profit Before Expenses",
                  value: calculations.profitBeforeExpenses,
                },
                {
                  title: "Profit After Expenses",
                  value: calculations.profitAfterExpenses,
                },
              ].map((stat, index) => (
                <Col xs={24} sm={12} md={6} key={index}>
                  <Statistic
                    title={stat.title}
                    value={stat.value.toFixed(2)}
                    prefix={
                      <span style={{ fontSize: "22px", color: "#fff" }}>₹</span>
                    }
                    valueStyle={{ color: "#fff" }}
                  />
                </Col>
              ))}
            </Row>
            <div className="profit-margin">
              Profit Margin: {calculations.profitMargin.toFixed(2)}%
            </div>
          </Card>
        </Col>

        {/* Orders Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Orders"
            value={0}
            icon={ShoppingCartOutlined}
            gradient="linear-gradient(135deg, #ff9a8b, #ff6a00)"
          />
        </Col>

        {/* Returns Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Returns"
            value={0}
            icon={FallOutlined}
            gradient="linear-gradient(135deg, #f8d7da, #f5c6cb)"
            color="#000"
          />
        </Col>

        {/* Suppliers Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Suppliers"
            value={dashboardData.supplier.totalSuppliers}
            icon={CreditCardOutlined}
            gradient="linear-gradient(135deg, #fdc830, #f37335)"
            onClick={() => navigate("/supplier")}
          />
        </Col>

        {/* Search Products Card */}
        <Col xs={24} sm={12} md={8}>
          <StatCard
            title="Search Products"
            value={dashboardData.stocks.totalStockLevel.toFixed(2)}
            prefix={<FundOutlined style={{ color: "#fff" }} />}
            icon={FundOutlined}
            gradient="linear-gradient(135deg, #fdc830, #f37335)"
            onClick={() => navigate("/products")}
          />
        </Col>
      </Row>

      {/* Uncomment PieChart if needed */}
      {/* <div style={{ padding: '0px', marginTop: '10px' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Sales, Purchases, Stocks, and Profit Overview" bordered={false} style={{ boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
              <ResponsiveContainer width="100%" height={700}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={250}
                    innerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value, percent }) => `${name}: ${value.toLocaleString()} (${(percent * 100).toFixed(2)}%)`}
                    paddingAngle={3}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </div> */}
    </div>
  );
};

export default Dashboard;
